<script setup lang="ts">
    import { computed, reactive, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { inviteUser, UserAccountRole, type RoleSummary, type AccountUserInvite } from './AccountUsers.access';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';

    const props = defineProps<{
        show: boolean;
        roles: RoleSummary[];
    }>();

    const emit = defineEmits<{
        close: [];
        invited: [invite: AccountUserInvite];
    }>();

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const extractEmailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const maxBulkEmails = 20;

    const state = reactive({
        email: '',
        isBulkInvite: false,
        dirtyEmails: '',
        selectedRole: '' as '' | 'administrator' | 'user' | string,
        isBusy: false,
    });

    watch(
        () => props.show,
        (show) => {
            if (!show) return;
            state.email = '';
            state.isBulkInvite = false;
            state.dirtyEmails = '';
            state.selectedRole = '';
        }
    );

    const roleOptions = computed(() => [
        { value: 'administrator', label: $Resources.UserProfileRoleAdministrator },
        { value: 'user', label: $Resources.UserProfileRoleUser },
        ...props.roles.map((role) => ({ value: role.RoleId, label: role.Name + (role.InternalDescription ? ` (${role.InternalDescription})` : '') })),
    ]);

    const cleanEmails = computed(() => {
        const matches = state.dirtyEmails.match(extractEmailRegex) || [];
        return matches.filter((email, index, self) => self.indexOf(email) === index).slice(0, maxBulkEmails);
    });

    const isValid = computed(() => {
        if (!state.selectedRole) return false;
        return state.isBulkInvite ? cleanEmails.value.length > 0 : emailRegex.test(state.email);
    });

    function close(): void {
        emit('close');
    }

    function getRoleTypeAndId(): { roleType: UserAccountRole; roleId: string | null } {
        if (state.selectedRole === 'administrator') return { roleType: UserAccountRole.Administrator, roleId: null };
        if (state.selectedRole === 'user') return { roleType: UserAccountRole.User, roleId: null };
        return { roleType: UserAccountRole.Custom, roleId: state.selectedRole };
    }

    async function onInvite(): Promise<void> {
        if (!isValid.value || state.isBusy) return;

        state.isBusy = true;
        const { roleType, roleId } = getRoleTypeAndId();

        try {
            if (state.isBulkInvite) {
                for (const email of cleanEmails.value) {
                    try {
                        const invite = await inviteUser(email, roleType, roleId);
                        emit('invited', invite);
                        bsAlert({
                            type: AlertTypes.Success,
                            title: $Resources.AccountUserInviteSentSuccessHeader,
                            content: $Resources.AccountUserInviteSentSuccessBody + invite.Email,
                        });
                    } catch {
                        bsAlert({ type: AlertTypes.Danger, title: $Resources.AccountUserInviteSentFailedHeader });
                    }
                }

                close();
            } else {
                const invite = await inviteUser(state.email.trim(), roleType, roleId);
                emit('invited', invite);
                close();
                bsAlert({
                    type: AlertTypes.Success,
                    title: $Resources.AccountUserInviteSentSuccessHeader,
                    content: $Resources.AccountUserInviteSentSuccessBody + invite.Email,
                });
            }
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.AccountUserInviteSentFailedHeader });
        } finally {
            state.isBusy = false;
        }
    }
</script>

<template>
    <Modal :show="props.show" :title="$Resources.InviteUserHeading" @close="close" hideHeader hideFooter>
        <template #content>
            <button type="button" class="btn btn-default btn-modal-close p-absolute round" @click="close">
                <NavIcons icon="close"></NavIcons>
                <span class="sr-only">{{ $Resources.CloseButton }}</span>
            </button>

            <div class="d-flex justify-content-center">
                <span class="profile-avatar profile-avatar-x3 round">
                    <span class="gravatar round"></span>
                </span>
            </div>

            <h3 class="text-center">{{ $Resources.InviteUserHeading }}</h3>
            <p class="text-center m-xl-b">{{ $Resources.InviteUserText }}</p>

            <div class="form-group">
                <FormCheckable
                    :checked="state.isBulkInvite"
                    :label="$Resources.BulkInviteEnableLabel"
                    :switchStyle="SwitchStyle.Standard"
                    @update:modelValue="state.isBulkInvite = !!$event" />
            </div>

            <FormInput
                v-if="!state.isBulkInvite"
                class="form-group"
                type="email"
                :label="$Resources.InviteUserEmailLabel"
                :placeholder="$Resources.InviteUserEmailPlaceholder"
                required
                :modelValue="state.email"
                @update:modelValue="state.email = $event" />

            <template v-else>
                <FormTextArea
                    class="form-group"
                    :label="$Resources.BulkInvitePasteEmailsLabel"
                    :placeholder="$Resources.InviteUserEmailPlaceholder"
                    :rows="5"
                    required
                    :modelValue="state.dirtyEmails"
                    @update:modelValue="state.dirtyEmails = $event" />

                <div v-if="cleanEmails.length > 0" class="form-group">
                    <h4 class="h5 d-flex justify-content-between">
                        {{ $Resources.BulkInviteSendToEmailsHeading }}:
                        <span>({{ cleanEmails.length }}/{{ maxBulkEmails }})</span>
                    </h4>
                    <ul class="list-border-divider list-item-state-hover list-unstyled m-lg-b">
                        <li class="p-sm" v-for="email in cleanEmails" :key="email">{{ email }}</li>
                    </ul>
                </div>
            </template>

            <div class="form-group">
                <FormRadioGroup
                    :legend="(state.isBulkInvite ? $Resources.BulkInviteUserRoleLabel : $Resources.InviteUserRoleLabel) + ':'"
                    :options="roleOptions"
                    :modelValue="state.selectedRole"
                    @update:modelValue="state.selectedRole = $event" />
            </div>

            <div class="btn-container form-group p-sm-t">
                <button
                    type="button"
                    class="btn btn-primary btn-block btn-lg"
                    :class="{ 'is-busy': state.isBusy }"
                    :disabled="!isValid || state.isBusy"
                    @click="onInvite">
                    {{ $Resources.InviteUserBtnText }}
                </button>
            </div>
        </template>
    </Modal>
</template>

